import CurrentUserModel from '../../Interfaces/Auth/CurrentUserModel';
import RunningUserModel from '../../Interfaces/Auth/RunningUserModel';

type CapabilityUserSource = 'user' | 'runningUser';

type CapabilityRule = {
    userSource: CapabilityUserSource;
    requiresSystemAccount: boolean;
    requiresSecurityAdmin: boolean;
    requiresTechTeam: boolean;
};

export const ADMINISTRATION_MANAGE_USERS = 'Administration.ManageUsers';
export const ADMINISTRATION_MANAGE_TECH_OBJECTS = 'Administration.ManageTechObjects';
export const ADMINISTRATION_MANAGE_LINKAGES = 'Administration.ManageLinkages';
export const REPORTING_AD_HOC_BUILDER = 'Reporting.AdHocBuilder';
export const AI_EXPLAIN = 'AI.Explain';
export const MAINTENANCE_BI_TOOLS = 'Maintenance.BITools';
export const ACCOUNT_IMPERSONATE_USER = 'Account.ImpersonateUser';
export const ACCOUNT_VIEW_DIAGNOSTIC_INFORMATION = 'Account.ViewDiagnosticInformation';

const capabilityRules: Record<string, CapabilityRule> = {
    [ADMINISTRATION_MANAGE_USERS]: {
        userSource: 'runningUser',
        requiresSystemAccount: false,
        requiresSecurityAdmin: true,
        requiresTechTeam: false,
    },
    [ADMINISTRATION_MANAGE_TECH_OBJECTS]: {
        userSource: 'runningUser',
        requiresSystemAccount: false,
        requiresSecurityAdmin: true,
        requiresTechTeam: true,
    },
    [ADMINISTRATION_MANAGE_LINKAGES]: {
        userSource: 'runningUser',
        requiresSystemAccount: false,
        requiresSecurityAdmin: true,
        requiresTechTeam: true,
    },
    [REPORTING_AD_HOC_BUILDER]: {
        userSource: 'runningUser',
        requiresSystemAccount: true,
        requiresSecurityAdmin: false,
        requiresTechTeam: false,
    },
    [AI_EXPLAIN]: {
        userSource: 'user',
        requiresSystemAccount: false,
        requiresSecurityAdmin: false,
        requiresTechTeam: false,
    },
    [MAINTENANCE_BI_TOOLS]: {
        userSource: 'user',
        requiresSystemAccount: true,
        requiresSecurityAdmin: true,
        requiresTechTeam: true,
    },
    [ACCOUNT_IMPERSONATE_USER]: {
        userSource: 'user',
        requiresSystemAccount: true,
        requiresSecurityAdmin: false,
        requiresTechTeam: false,
    },
    [ACCOUNT_VIEW_DIAGNOSTIC_INFORMATION]: {
        userSource: 'user',
        requiresSystemAccount: true,
        requiresSecurityAdmin: true,
        requiresTechTeam: true,
    },
};

const getCapabilityUser = (
    user: CurrentUserModel | null | undefined,
    userSource: CapabilityUserSource
): CurrentUserModel | RunningUserModel | null | undefined => {
    if (userSource === 'runningUser') {
        return user?.runningUser;
    }

    return user;
};

export const hasCapability = (
    user: CurrentUserModel | null | undefined,
    capability: string
) => {
    const rule = capabilityRules[capability];

    if (!rule) {
        return false;
    }

    const capabilityUser = getCapabilityUser(user, rule.userSource);

    if (!capabilityUser) {
        return false;
    }

    return (!rule.requiresSystemAccount ||
            capabilityUser.isSystemAccount === true) &&
            (!rule.requiresSecurityAdmin ||
             capabilityUser.isSecurityAdmin === true) &&
            (!rule.requiresTechTeam ||
             capabilityUser.isTechTeam === true);
};

export const canManageUsers = (user?: CurrentUserModel | null) => hasCapability(user, ADMINISTRATION_MANAGE_USERS);

export const canManageTechAdministration = (user?: CurrentUserModel | null) => hasCapability(user, ADMINISTRATION_MANAGE_TECH_OBJECTS);

export const canManageLinkages = (user?: CurrentUserModel | null) => hasCapability(user, ADMINISTRATION_MANAGE_LINKAGES);

export const canUseAdHocReportBuilder = (user?: CurrentUserModel | null) => hasCapability(user, REPORTING_AD_HOC_BUILDER);

export const canUseAIExplain = (user?: CurrentUserModel | null) => hasCapability(user, AI_EXPLAIN);

export const canUseBIMaintenance = (user?: CurrentUserModel | null) => hasCapability(user, MAINTENANCE_BI_TOOLS);

export const canViewDiagnosticInformation = (user?: CurrentUserModel | null) => hasCapability(user, ACCOUNT_VIEW_DIAGNOSTIC_INFORMATION);

export const canImpersonateUser = (user?: CurrentUserModel | null) => hasCapability(user, ACCOUNT_IMPERSONATE_USER);
